import { SparklesIcon } from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';

import styles from '../agents.module.css';

import clsxm from '@/lib/clsxm';

import { resolveChannelIcon } from '@/components/bots/Integration/ChannelLogo';

import type { IndustryContent, WorkflowItem } from './types';
import { ArrowGlyph, delay, SectionHeading, Stage, tone } from '../primitives';
import { useReveal } from '../useReveal';

/** What the agent learns from, what it can do, and where it answers. */
export default function IndustryWorkflow({
  workflow,
}: {
  workflow: IndustryContent['workflow'];
}) {
  const { t } = useLocale('bot-use-cases');
  const { ref, state } = useReveal<HTMLDivElement>(0.2);

  return (
    <section id='how-it-works' className='py-6 sm:py-10'>
      <div className='mx-auto max-w-7xl px-3 sm:px-6 lg:px-8'>
        <Stage
          variant='lavender'
          halftoneAt='0% 100%'
          halftoneReach={70}
          className='px-5 py-14 sm:px-12 sm:py-20'
        >
          <SectionHeading
            align='center'
            title={workflow.title}
            tail={workflow.tail}
          />

          <div
            ref={ref}
            data-reveal={state}
            className={clsxm(
              styles.reveal,
              'relative mt-14 grid items-stretch gap-3 lg:grid-cols-[1fr_auto_1.1fr_auto_1fr]',
            )}
          >
            <div className={styles.rv} style={delay(0)}>
              <Column title={t('Learns from')} items={workflow.learns} />
            </div>
            <Connector at={150} />
            <div className={styles.rv} style={delay(250)}>
              <AgentColumn title={t('Acts with')} items={workflow.acts} />
            </div>
            <Connector at={400} />
            <div className={styles.rv} style={delay(500)}>
              <Column title={t('Answers on')} items={workflow.channels} />
            </div>
          </div>
        </Stage>
      </div>
    </section>
  );
}

function ItemIcon({
  icon,
  inverted,
}: {
  icon: WorkflowItem['icon'];
  inverted?: boolean;
}) {
  if (typeof icon === 'string') {
    return (
      <span className='flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-[#f3f3f7] dark:bg-white'>
        {resolveChannelIcon(icon, 'h-4 w-4')}
      </span>
    );
  }
  const Icon = icon;
  return (
    <span
      className={clsxm(
        'flex h-8 w-8 flex-none items-center justify-center rounded-lg',
        inverted
          ? 'bg-white/[0.12] text-white'
          : 'bg-primary-50 text-primary-600 dark:bg-primary-400/10 dark:text-primary-300',
      )}
    >
      <Icon className='h-4 w-4' />
    </span>
  );
}

function Column({ title, items }: { title: string; items: WorkflowItem[] }) {
  return (
    <div
      className={clsxm(
        'h-full rounded-[22px] p-5 shadow-[0_24px_60px_-34px_rgba(30,20,90,0.35)] sm:p-6',
        tone.surface,
        tone.line,
      )}
    >
      <p
        className={clsxm(
          'text-xs font-semibold uppercase tracking-[0.08em]',
          tone.quiet,
        )}
      >
        {title}
      </p>
      <ul className='mt-4 space-y-2.5'>
        {items.map(({ icon, label }) => (
          <li key={label} className='flex items-center gap-3'>
            <ItemIcon icon={icon} />
            <span className={clsxm('text-[15px] font-medium', tone.ink)}>
              {label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The agent itself, in the middle, with what it can do. */
function AgentColumn({
  title,
  items,
}: {
  title: string;
  items: WorkflowItem[];
}) {
  const { t } = useLocale('bot-use-cases');

  return (
    <div className='relative h-full overflow-hidden rounded-[22px] bg-[linear-gradient(160deg,rgb(var(--tw-color-primary-600))_0%,rgb(var(--tw-color-primary-800))_100%)] p-5 text-white shadow-[0_30px_70px_-30px_rgba(40,30,140,0.7)] ring-1 ring-inset ring-white/10 sm:p-6'>
      <p className='flex items-center gap-3'>
        <span className='flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-white text-primary-700'>
          <SparklesIcon className='h-5 w-5' />
        </span>
        <span className='text-lg font-semibold'>{t('Your agent')}</span>
      </p>
      <p className='mt-6 text-xs font-semibold uppercase tracking-[0.08em] text-primary-100/80'>
        {title}
      </p>
      <ul className='mt-4 space-y-2.5'>
        {items.map(({ icon, label }) => (
          <li key={label} className='flex items-center gap-3'>
            <ItemIcon icon={icon} inverted />
            <span className='text-[15px] font-medium'>{label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Connector({ at }: { at: number }) {
  return (
    <div
      aria-hidden='true'
      className={clsxm(styles.rv, 'flex items-center justify-center py-1')}
      style={delay(at)}
    >
      <span
        className={clsxm(
          'flex h-9 w-9 items-center justify-center rounded-full',
          tone.surface,
          tone.line,
        )}
      >
        {/* Down when stacked, along the reading direction side by side. */}
        <ArrowGlyph className='h-4 w-4 rotate-90 text-primary-600 rtl:-rotate-90 dark:text-primary-400 lg:rotate-0 lg:rtl:rotate-0' />
      </span>
    </div>
  );
}
